import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Lock, LogOut, Pencil, Plus, Trash2, X, Zap, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-500 outline-none transition-colors focus:border-neon-lime/60 focus:ring-1 focus:ring-neon-lime/40";

const emptyEvent = { name: "", date: "", time: "", venue: "", neighborhood: "", category: "Club", price: "", status: "RSVP Open", image: "", description: "", city: "phl", capacity: 100 };
const emptySpot = { name: "", neighborhood: "", style: "Late-Night Eats", hours: "", rating: 4.5, price_tier: "$$", featured_dish: "", image: "", description: "", city: "phl" };
const CITY_OPTIONS = ["phl", "atx", "bna"];
const emptyCity = { name: "", state: "", coords: "", timezone: "America/New_York", pass_prefix: "", hero_image: "", tagline: "" };

const cityFields = [
  { key: "name", label: "City Name" },
  { key: "state", label: "State" },
  { key: "coords", label: "Coordinates (e.g. 39.95°N 75.16°W)" },
  { key: "pass_prefix", label: "Pass Prefix (e.g. PHL)" },
  { key: "timezone", label: "Timezone", type: "select", options: ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"] },
  { key: "hero_image", label: "Hero Image URL", full: true },
  { key: "tagline", label: "Tagline", type: "textarea" },
];

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</span>
      <input {...props} className={inputCls} />
    </label>
  );
}

function FormModal({ title, fields, values, setValues, onSubmit, onClose, saveTestId }) {
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSubmit();
    setSaving(false);
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
        className="my-8 w-full max-w-lg rounded-2xl border border-white/10 bg-[#0E1117] p-6 sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight">{title}</h3>
          <button onClick={onClose} data-testid="admin-form-close" className="rounded-full border border-white/10 p-2 text-zinc-400 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.map((f) =>
            f.type === "checkbox" ? (
              <label key={f.key} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 sm:col-span-2">
                <input type="checkbox" checked={!!values[f.key]}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.checked }))}
                  className="h-4 w-4 accent-purple-500" data-testid={`admin-field-${f.key}`} />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">{f.label}</span>
              </label>
            ) : f.type === "select" ? (
              <label key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{f.label}</span>
                <select value={values[f.key]} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className={inputCls} data-testid={`admin-field-${f.key}`}>
                  {f.options.map((o) => <option key={o} value={o} className="bg-[#0E1117]">{o}</option>)}
                </select>
              </label>
            ) : f.type === "textarea" ? (
              <label key={f.key} className="sm:col-span-2">
                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{f.label}</span>
                <textarea rows={3} value={values[f.key]} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className={inputCls} data-testid={`admin-field-${f.key}`} />
              </label>
            ) : (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Field label={f.label} type={f.type || "text"} required={f.required !== false} step={f.step}
                  value={values[f.key]} onChange={(e) => setValues((v) => ({ ...v, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value }))}
                  data-testid={`admin-field-${f.key}`} />
              </div>
            )
          )}
          <button type="submit" disabled={saving} data-testid={saveTestId}
            className="flex items-center justify-center gap-2 rounded-full bg-neon-lime py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-60 sm:col-span-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

const eventFields = [
  { key: "name", label: "Event Name", full: true },
  { key: "date", label: "Date", type: "date" },
  { key: "time", label: "Time" },
  { key: "venue", label: "Venue" },
  { key: "neighborhood", label: "Neighborhood" },
  { key: "category", label: "Category", type: "select", options: ["Club", "Live", "Food", "Art", "LGBTQ+", "Lifestyle (LS)"] },
  { key: "status", label: "Status", type: "select", options: ["Open", "Selling Fast", "Guestlist Only"] },
  { key: "price", label: "Price" },
  { key: "capacity", label: "Capacity", type: "number" },
  { key: "external_link", label: "External Ticket Link (button goes to vendor's ticket page)", full: true },
  { key: "members_only", label: "Members Only (email verification required to view venue)", type: "checkbox" },
  { key: "recurring", label: "Recurring weekly — auto-reposts 7 days out after each date", type: "checkbox" },
  { key: "city", label: "City", type: "select", options: CITY_OPTIONS },
  { key: "image", label: "Image URL", full: true },
  { key: "description", label: "Description", type: "textarea" },
];

const spotFields = [
  { key: "name", label: "Spot Name", full: true },
  { key: "neighborhood", label: "Neighborhood" },
  { key: "style", label: "Style", type: "select", options: ["Street Food", "Speakeasy", "Late-Night Eats", "Rooftop Bar"] },
  { key: "hours", label: "Hours" },
  { key: "rating", label: "Rating", type: "number", step: "0.1" },
  { key: "price_tier", label: "Price Tier", type: "select", options: ["$", "$$", "$$$"] },
  { key: "featured_dish", label: "Featured Dish" },
  { key: "city", label: "City", type: "select", options: CITY_OPTIONS },
  { key: "image", label: "Image URL", full: true },
  { key: "description", label: "Description", type: "textarea" },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState("events");
  const [cityFilter, setCityFilter] = useState("phl");
  const [events, setEvents] = useState([]);
  const [spots, setSpots] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adminCities, setAdminCities] = useState([]);
  const [subs, setSubs] = useState([]);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);

  const loadAll = () => {
    api.get(`/events?city=${cityFilter}`).then(({ data }) => setEvents(data));
    api.get(`/food-spots?city=${cityFilter}`).then(({ data }) => setSpots(data));
    api.get("/admin/cities").then(({ data }) => setAdminCities(data)).catch(() => {});
    api.get("/admin/submissions").then(({ data }) => setSubs(data)).catch(() => {});
    api.get("/admin/members").then(({ data }) => setMembers(data)).catch(() => {});
  };

  useEffect(() => {
    if (user) loadAll();
  }, [user, cityFilter]);

  const toggleCity = async (id) => {
    try {
      const { data } = await api.put(`/admin/cities/${id}/live`);
      toast.success(data.live ? "City launched — visitors can see it now." : "City hidden from visitors.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reviewSub = async (id, action) => {
    try {
      await api.post(`/admin/submissions/${id}/${action}`);
      toast.success(action === "approve" ? "Published to the city." : "Submission rejected.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveCity = async () => {
    try {
      await api.post("/admin/cities", { ...editing.data, guide: [] });
      toast.success("City created — it's Hidden until you launch it.");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [parsing, setParsing] = useState(false);

  const parseEvent = async () => {
    setParsing(true);
    try {
      const { data } = await api.post("/admin/parse-event", { text: importText });
      setEditing({ kind: "event", data: { ...emptyEvent, city: cityFilter, ...data } });
      setImportOpen(false);
      setImportText("");
      toast.success("Parsed — review the form and save.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setParsing(false);
    }
  };

  const pendingSubs = subs.filter((s) => s.status === "pending");
  const cityIds = adminCities.length ? adminCities.map((c) => c.id) : CITY_OPTIONS;
  const cityLabel = (id) =>
    adminCities.find((c) => c.id === id)?.name ||
    ({ phl: "Philadelphia", atx: "Austin", bna: "Nashville" })[id] ||
    id;

  const [toolMsg, setToolMsg] = useState("");
  const [toolBusy, setToolBusy] = useState("");

  const runDispatch = async () => {
    setToolBusy("dispatch");
    setToolMsg("");
    try {
      const { data } = await api.post("/admin/newsletter/dispatch");
      setToolMsg(`Dispatch sent to ${data.sent} of ${data.subscribers} subscribers with ${data.events} upcoming events.`);
    } catch (err) {
      setToolMsg(formatApiError(err));
    } finally {
      setToolBusy("");
    }
  };

  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const sendForgot = async () => {
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setForgotBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password updated — use it on your next login.");
      setPwOpen(false);
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwBusy(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      setUser(data);
      toast.success("Welcome back, city manager.");
    } catch (err) {
      setLoginError(formatApiError(err));
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const saveEvent = async () => {
    try {
      if (editing.data.id) await api.put(`/admin/events/${editing.data.id}`, editing.data);
      else await api.post("/admin/events", editing.data);
      toast.success("Event saved.");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSpot = async () => {
    try {
      if (editing.data.id) await api.put(`/admin/food-spots/${editing.data.id}`, editing.data);
      else await api.post("/admin/food-spots", editing.data);
      toast.success("Spot saved.");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (kind, id) => {
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Deleted.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-neon-lime" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4" data-testid="admin-login-page">
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          onSubmit={login}
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0E1117] p-8"
        >
          <div className="mb-8 text-center">
            <Zap className="mx-auto h-8 w-8 text-neon-lime" fill="currentColor" />
            <h1 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight">City Control</h1>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Philly After Dark — Admin</p>
          </div>
          <div className="space-y-4">
            <input type="email" required value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="Admin email" data-testid="admin-email-input" className={inputCls} />
            <input type="password" required value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="Password" data-testid="admin-password-input" className={inputCls} />
            {loginError && (
              <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-400" data-testid="admin-login-error">{loginError}</p>
            )}
            <button type="submit" disabled={loginLoading} data-testid="admin-login-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-neon-lime py-3.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-60">
              {loginLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Enter Control Room
            </button>
            <button type="button" onClick={() => { setForgotOpen((o) => !o); setForgotSent(false); }} data-testid="admin-forgot-toggle"
              className="w-full text-center font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 transition-colors hover:text-neon-lime">
              Forgot password?
            </button>
            {forgotOpen && (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="forgot-form">
                {forgotSent ? (
                  <p className="text-center font-mono text-xs uppercase tracking-[0.2em] text-neon-lime" data-testid="forgot-sent-message">
                    If that email is an admin, a reset link is on its way
                  </p>
                ) : (
                  <div className="space-y-3">
                    <input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Your admin email" data-testid="forgot-email-input" className={inputCls} />
                    <button type="button" onClick={sendForgot} disabled={forgotBusy} data-testid="forgot-send-button"
                      className="flex w-full items-center justify-center gap-2 rounded-full border border-neon-lime/50 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-neon-lime transition-all hover:bg-neon-lime hover:text-black disabled:opacity-60">
                      {forgotBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email Me a Reset Link"}
                    </button>
                  </div>
                )}
              </div>
            )}
            <a href="/" data-testid="admin-back-home-link" className="block text-center font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600 hover:text-zinc-300">
              ← Back to the city
            </a>
          </div>
        </motion.form>
      </div>
    );
  }

  const tabs = [
    { key: "events", label: `Events (${events.length})` },
    { key: "food", label: `Food Spots (${spots.length})` },
    { key: "members", label: `Members (${members.length})` },
    { key: "approvals", label: `Approvals (${pendingSubs.length})` },
    { key: "tools", label: "Emails & Tools" },
  ];

  return (
    <div className="min-h-screen px-5 py-10 sm:px-8" data-testid="admin-dashboard-container">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-neon-lime">City Control Room</div>
            <h1 className="mt-2 font-editorial text-3xl uppercase tracking-wide">Manage the Night</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden font-mono text-xs text-zinc-500 sm:block">{user.email}</span>
            <a href="/" data-testid="admin-view-site-link" className="rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 hover:border-white/40">
              View Site
            </a>
            <button onClick={() => setPwOpen(true)} data-testid="admin-password-change-button"
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-neon-lime/50 hover:text-neon-lime">
              <Lock className="h-3.5 w-3.5" /> Password
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-red-500/40 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-red-400 hover:bg-red-500/10">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="admin-city-filter">
          <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">City:</span>
          {cityIds.map((c) => (
            <button key={c} onClick={() => setCityFilter(c)} data-testid={`admin-city-${c}`}
              className={`rounded-full px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-all ${
                cityFilter === c ? "bg-neon-cyan font-bold text-black" : "border border-white/15 text-zinc-400 hover:text-white"
              }`}>
              {cityLabel(c)}
            </button>
          ))}
        </div>

        <div className="mb-8 flex flex-wrap items-center gap-3" data-testid="admin-city-launch-bar">
          <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">Launch control:</span>
          {adminCities.map((c) => (
            <button key={c.id} onClick={() => toggleCity(c.id)} data-testid={`admin-city-toggle-${c.id}`}
              className={`flex items-center gap-2 rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-all ${
                c.live
                  ? "border-neon-lime/50 bg-neon-lime/10 text-neon-lime hover:bg-neon-lime/20"
                  : "border-white/15 text-zinc-500 hover:border-neon-amber/50 hover:text-neon-amber"
              }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${c.live ? "bg-neon-lime animate-pulse-glow" : "bg-zinc-600"}`} />
              {c.name} — {c.live ? "Live" : "Hidden"}
            </button>
          ))}
          <button onClick={() => setEditing({ kind: "city", data: { ...emptyCity } })} data-testid="admin-add-city-button"
            className="flex items-center gap-2 rounded-full border border-dashed border-white/30 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 transition-all hover:border-neon-lime/60 hover:text-neon-lime">
            <Plus className="h-3.5 w-3.5" /> Add City
          </button>
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-all ${
                tab === t.key ? "bg-neon-lime font-bold text-black" : "border border-white/15 text-zinc-400 hover:text-white"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "events" && (
          <div>
            <div className="mb-6 flex flex-wrap gap-3">
              <button onClick={() => setEditing({ kind: "event", data: { ...emptyEvent, city: cityFilter } })} data-testid="admin-add-event-button"
                className="flex items-center gap-2 rounded-full bg-neon-lime px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-all hover:-translate-y-0.5">
                <Plus className="h-4 w-4" /> Add Event
              </button>
              <button onClick={() => setImportOpen(true)} data-testid="admin-ai-import-button"
                className="flex items-center gap-2 rounded-full border border-neon-purple/50 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-neon-purple transition-all hover:-translate-y-0.5 hover:bg-neon-purple hover:text-black">
                <Zap className="h-4 w-4" /> AI Import from Social
              </button>
            </div>
            <div className="space-y-3">
              {events.map((e) => (
                <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#131722] px-5 py-4" data-testid={`admin-event-row-${e.id}`}>
                  <div>
                    <div className="font-display text-sm font-bold uppercase">{e.name}</div>
                    <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                      {e.date} — {e.time} — {e.venue} — <span className="text-neon-cyan">{e.status}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ kind: "event", data: { ...e } })} data-testid={`admin-edit-event-${e.id}`}
                      className="rounded-full border border-white/15 p-2.5 text-zinc-400 hover:border-neon-lime/50 hover:text-neon-lime"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove("events", e.id)} data-testid={`admin-delete-item-button-${e.id}`}
                      className="rounded-full border border-white/15 p-2.5 text-zinc-400 hover:border-red-500/50 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "food" && (
          <div>
            <button onClick={() => setEditing({ kind: "spot", data: { ...emptySpot, city: cityFilter } })} data-testid="admin-add-food-button"
              className="mb-6 flex items-center gap-2 rounded-full bg-neon-lime px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-all hover:-translate-y-0.5">
              <Plus className="h-4 w-4" /> Add Food Spot
            </button>
            <div className="space-y-3">
              {spots.map((s) => (
                <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#131722] px-5 py-4" data-testid={`admin-food-row-${s.id}`}>
                  <div>
                    <div className="font-display text-sm font-bold uppercase">{s.name}</div>
                    <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                      {s.neighborhood} — {s.style} — {s.hours} — <span className="text-neon-amber">{s.rating}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ kind: "spot", data: { ...s } })} data-testid={`admin-edit-food-${s.id}`}
                      className="rounded-full border border-white/15 p-2.5 text-zinc-400 hover:border-neon-lime/50 hover:text-neon-lime"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove("food-spots", s.id)} data-testid={`admin-delete-food-${s.id}`}
                      className="rounded-full border border-white/15 p-2.5 text-zinc-400 hover:border-red-500/50 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "tools" && (
          <div className="max-w-xl" data-testid="admin-tools-tab">
            <div className="rounded-2xl border border-white/10 bg-[#131722] p-6">
              <Mail className="h-6 w-6 text-neon-lime" />
              <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-tight">Friday Dispatch</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                Emails every newsletter subscriber the five best upcoming events across all live cities. One click, every week.
              </p>
              <button onClick={runDispatch} disabled={toolBusy === "dispatch"} data-testid="admin-dispatch-button"
                className="mt-5 flex items-center gap-2 rounded-full bg-neon-lime px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-60">
                {toolBusy === "dispatch" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {toolBusy === "dispatch" ? "Sending..." : "Send Dispatch Now"}
              </button>
            </div>
            {toolMsg && (
              <p className="mt-6 rounded-lg border border-neon-lime/30 bg-neon-lime/5 px-4 py-3 font-mono text-xs text-neon-lime" data-testid="admin-tools-result">
                {toolMsg}
              </p>
            )}
          </div>
        )}

        {tab === "members" && (
          <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="admin-members-table">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead className="border-b border-white/10 bg-[#0E1117] font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                <tr>
                  {["Email", "First Verified", "Last Verified"].map((h) => (
                    <th key={h} className="px-5 py-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.email} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`admin-member-row-${m.email}`}>
                    <td className="px-5 py-4 text-neon-purple">{m.email}</td>
                    <td className="px-5 py-4 font-mono text-xs text-zinc-500">{m.first_verified ? new Date(m.first_verified).toLocaleString() : "—"}</td>
                    <td className="px-5 py-4 font-mono text-xs text-zinc-500">{m.last_verify ? new Date(m.last_verify).toLocaleString() : "—"}</td>
                  </tr>
                ))}
                {members.length === 0 && (
                  <tr><td colSpan={3} className="px-5 py-12 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-600">No verified members yet — they appear here after email verification</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "approvals" && (
          <div data-testid="admin-approvals-tab">
            {pendingSubs.length === 0 ? (
              <p className="rounded-xl border border-white/10 px-5 py-12 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-600" data-testid="approvals-empty">
                No pending tips — the streets are quiet
              </p>
            ) : (
              <div className="space-y-3">
                {pendingSubs.map((s) => (
                  <div key={s.id} className="rounded-xl border border-neon-amber/25 bg-[#131722] px-5 py-4" data-testid={`admin-submission-row-${s.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className={`rounded-full border px-3 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${
                            s.kind === "event" ? "border-neon-lime/50 text-neon-lime" : "border-neon-magenta/50 text-neon-magenta"
                          }`}>
                            {s.kind === "event" ? "Event" : "Food Spot"}
                          </span>
                          <span className="font-display text-sm font-bold uppercase">{s.name}</span>
                        </div>
                        <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                          {cityLabel(s.city)} — {s.neighborhood}
                          {s.kind === "event" ? ` — ${s.date} ${s.time} @ ${s.venue}` : ` — ${s.style} — ${s.hours}`}
                        </div>
                        <p className="mt-2 max-w-2xl text-sm text-zinc-400">{s.description}</p>
                        <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600">
                          Tipped by {s.submitter_name} ({s.submitter_email})
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => reviewSub(s.id, "approve")} data-testid={`admin-approve-submission-${s.id}`}
                          className="rounded-full bg-neon-lime px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-all hover:-translate-y-0.5">
                          Approve & Publish
                        </button>
                        <button onClick={() => reviewSub(s.id, "reject")} data-testid={`admin-reject-submission-${s.id}`}
                          className="rounded-full border border-red-500/40 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-red-400 hover:bg-red-500/10">
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {pwOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setPwOpen(false)}>
            <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0E1117] p-6 sm:p-8" data-testid="password-modal">
              <div className="mb-6 flex items-start justify-between">
                <div>
                  <div className="font-mono text-xs uppercase tracking-[0.3em] text-neon-lime">Security</div>
                  <h3 className="mt-2 font-display text-lg font-bold uppercase tracking-tight">Change Password</h3>
                  <p className="mt-1 text-sm text-zinc-500">Minimum 8 characters. It survives restarts and redeploys.</p>
                </div>
                <button onClick={() => setPwOpen(false)} data-testid="password-modal-close"
                  className="rounded-full border border-white/10 p-2 text-zinc-400 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
              <form onSubmit={changePassword} className="space-y-4">
                <input type="password" required value={pwForm.current}
                  onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
                  placeholder="Current password" data-testid="password-current-input" className={inputCls} />
                <input type="password" required minLength={8} value={pwForm.next}
                  onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
                  placeholder="New password (8+ chars)" data-testid="password-new-input" className={inputCls} />
                <input type="password" required minLength={8} value={pwForm.confirm}
                  onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                  placeholder="Confirm new password" data-testid="password-confirm-input" className={inputCls} />
                <button type="submit" disabled={pwBusy} data-testid="password-save-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-neon-lime py-3.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-60">
                  {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                  {pwBusy ? "Updating" : "Update Password"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
        {importOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setImportOpen(false)}>
            <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl border border-neon-purple/25 bg-[#0E1117] p-6 sm:p-8" data-testid="ai-import-modal">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <div className="font-mono text-xs uppercase tracking-[0.3em] text-neon-purple">AI Import</div>
                  <h3 className="mt-2 font-display text-lg font-bold uppercase tracking-tight">Paste the Post</h3>
                  <p className="mt-1 text-sm text-zinc-500">Caption, flyer text, or event blurb from Instagram, Facebook, anywhere.</p>
                </div>
                <button onClick={() => setImportOpen(false)} data-testid="ai-import-close"
                  className="rounded-full border border-white/10 p-2 text-zinc-400 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
              <textarea rows={7} value={importText} onChange={(e) => setImportText(e.target.value)}
                placeholder="e.g. THIS FRIDAY: Warehouse party at The Battery, Callowhill. Doors 11pm, $25, techno all night..."
                data-testid="ai-import-textarea"
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-neon-purple/60" />
              <button onClick={parseEvent} disabled={parsing || !importText.trim()} data-testid="ai-import-parse-button"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-neon-purple py-3.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-60">
                {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                {parsing ? "Reading the post..." : "Parse into Event Form"}
              </button>
            </motion.div>
          </motion.div>
        )}
        {editing?.kind === "event" && (
          <FormModal title={editing.data.id ? "Edit Event" : "Add Event"}
            fields={eventFields.map((f) => (f.key === "city" ? { ...f, options: cityIds } : f))}
            values={editing.data} setValues={(fn) => setEditing((e2) => ({ ...e2, data: fn(e2.data) }))}
            onSubmit={saveEvent} onClose={() => setEditing(null)} saveTestId="admin-event-save-button" />
        )}
        {editing?.kind === "spot" && (
          <FormModal title={editing.data.id ? "Edit Food Spot" : "Add Food Spot"}
            fields={spotFields.map((f) => (f.key === "city" ? { ...f, options: cityIds } : f))}
            values={editing.data} setValues={(fn) => setEditing((e2) => ({ ...e2, data: fn(e2.data) }))}
            onSubmit={saveSpot} onClose={() => setEditing(null)} saveTestId="admin-food-save-button" />
        )}
        {editing?.kind === "city" && (
          <FormModal title="Add City (starts Hidden)" fields={cityFields}
            values={editing.data} setValues={(fn) => setEditing((e2) => ({ ...e2, data: fn(e2.data) }))}
            onSubmit={saveCity} onClose={() => setEditing(null)} saveTestId="admin-city-save-button" />
        )}
      </AnimatePresence>
    </div>
  );
}
